/**
 * DatePickerDropdown Component
 *
 * Renders a date picker in a positioned dropdown for date-type filters.
 * Allows both calendar selection and manual date input.
 */

"use client";

import React from "react";
import {
  Box,
  Paper,
  Popper,
  Typography,
  Fade,
  Divider,
  ListItemButton,
  ListItemText,
  TextField,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type { Moment } from "moment";

import { DROPDOWN_TRANSITION_DURATION, DROPDOWN_Z_INDEX } from "../constants";
import { DATE_FORMAT } from "../../../../constants";

/**
 * Props for the DatePickerDropdown component
 */
interface DatePickerDropdownProps {
  /** Whether the date picker is visible */
  open: boolean;
  /** Reference element for positioning */
  anchorEl: HTMLElement | null;
  /** Label of the current filter (for header display) */
  filterLabel: string | undefined;
  /** Currently selected date */
  selectedDate: Moment | null;
  /** Callback when date selection changes */
  onDateChange: (date: Moment | null) => void;
  /** Callback when date is accepted/confirmed */
  onDateAccept: (date: Moment | null) => void;
  /** Callback when date picker is cancelled */
  onCancel: () => void;
}

/**
 * DatePickerDropdown renders a positioned date picker for date-type filters.
 * Features:
 * - Calendar view for visual date selection
 * - Text input for manual date entry (DD/MM/YYYY format)
 * - Action buttons: Clear, Today, Accept
 * - Cancel button to abort selection
 *
 * @param props - Component props
 * @returns Rendered date picker dropdown
 *
 * @example
 * <DatePickerDropdown
 *   open={datePickerOpen}
 *   anchorEl={containerRef.current}
 *   filterLabel="Expires"
 *   selectedDate={selectedDate}
 *   onDateChange={handleDateChange}
 *   onDateAccept={handleDateAccept}
 *   onCancel={handleCancel}
 * />
 */
export function DatePickerDropdown({
  open,
  anchorEl,
  filterLabel,
  selectedDate,
  onDateChange,
  onDateAccept,
  onCancel,
}: DatePickerDropdownProps) {
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      transition
      style={{ zIndex: DROPDOWN_Z_INDEX }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={DROPDOWN_TRANSITION_DURATION}>
          <Paper
            elevation={4}
            sx={{
              mt: 0.5,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {/* Header with filter name */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                bgcolor: "info.main",
                color: "info.contrastText",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <CalendarMonthIcon sx={{ fontSize: 18 }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {filterLabel}: Select date
              </Typography>
            </Box>

            {/* Date picker content */}
            <Box sx={{ p: 2 }}>
              <DatePicker
                value={selectedDate}
                onChange={onDateChange}
                onAccept={onDateAccept}
                inputFormat={DATE_FORMAT}
                renderInput={(params) => (
                  <TextField
                    // fullWidth
                    // name="log_time"
                    size="small"
                    placeholder={DATE_FORMAT}
                    fullWidth
                    sx={{ mb: 1 }}
                    {...params}
                  />
                )}
                // componentsProps={{
                //   textField: {
                //     size: "small",
                //     placeholder: DATE_FORMAT,
                //     fullWidth: true,
                //     sx: { mb: 1 },
                //   },
                //   actionBar: {
                //     // Available actions in the date picker toolbar
                //     actions: ["clear", "today", "accept"],
                //   },
                // }}
              />
              {/* Help text */}
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "block",
                  }}
                >
                  Select from calendar or type manually
                </Typography>
              </Box>
            </Box>

            {/* Cancel button */}
            <Divider />
            <ListItemButton
              onClick={onCancel}
              sx={{
                py: 0.75,
                px: 2,
                bgcolor: "grey.50",
                "&:hover": {
                  bgcolor: "grey.100",
                },
              }}
            >
              <ListItemText
                primary="Cancel"
                primaryTypographyProps={{
                  fontSize: 12,
                  color: "text.secondary",
                }}
              />
            </ListItemButton>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
}
